import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import LogoSpinner from '../components/ui/LogoSpinner';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { useExiting } from '../components/ui/motion';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Tabs, { TabPanel } from '../components/ui/Tabs';
import SegTrack from '../components/ui/SegTrack';
import Tag from '../components/ui/Tag';

// Sales approval surface for pilot assignment marks (feature 10). Pilots mark
// drones FOR a customer in the field (custody intent); this page is where Sales
// makes the lease/sale call and commits ownership (or rejects). The History tab
// is a read-only record of past decisions. Gated to sales/admin via the route +
// menuConfig; the API is capability-gated server-side.

interface TransferRequest {
  id: number;
  proposed_customer_org_id: number | null;
  marked_by_user_id: number | null;
  marked_at: string;
  customer_name: string | null;
  marked_by_name: string | null;
  drone_serial: string;
}

interface TransferHistoryRow {
  proposed_customer_org_id: number | null;
  status: string;
  lease_or_sale: string | null;
  reviewed_at: string;
  customer_name: string | null;
  reviewed_by_name: string | null;
  note: string | null;
  drone_serial: string;
}

interface PendingGroup {
  key: string;
  customer: string | null;
  pilot: string | null;
  date: string;
  ids: number[];
  serials: string[];
}

interface HistoryGroup {
  key: string;
  customer: string | null;
  status: string;
  leaseOrSale: string | null;
  reviewer: string | null;
  reviewedAt: string;
  note: string | null;
  serials: string[];
}

type LeaseOrSale = 'lease' | 'sale';

const dayKey = (iso?: string | null) => (iso || '').slice(0, 10);
const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const SerialChip = ({ serial }: { serial: string }) => (
  <Tag variant="code" mono size="sm">{serial}</Tag>
);

const CustomerTransfers: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [history, setHistory] = useState<TransferHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [choices, setChoices] = useState<Record<string, LeaseOrSale>>({}); // groupKey -> 'lease' | 'sale'
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/assignment-requests');
      setRequests(data || []);
    } catch {
      toast.error('Failed to load transfer requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // History is fetched lazily the first time the tab is opened, then refreshed
  // after any approve/reject so a just-decided batch shows up there immediately.
  const loadHistory = useCallback(async () => {
    try {
      const data = await api.get('/assignment-requests/history');
      setHistory(data || []);
      setHistoryLoaded(true);
    } catch {
      toast.error('Failed to load transfer history');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'history' && !historyLoaded) loadHistory();
  }, [tab, historyLoaded, loadHistory]);

  // Group pending rows by (customer, pilot, day) — one Approve/Reject per batch.
  const pendingGroups = () => {
    const groups: Record<string, PendingGroup> = {};
    for (const r of requests) {
      const key = `${r.proposed_customer_org_id}|${r.marked_by_user_id}|${dayKey(r.marked_at)}`;
      if (!groups[key]) {
        groups[key] = { key, customer: r.customer_name, pilot: r.marked_by_name, date: r.marked_at, ids: [], serials: [] };
      }
      groups[key].ids.push(r.id);
      groups[key].serials.push(r.drone_serial);
    }
    return Object.values(groups);
  };

  // Group history by the review action (customer, outcome, lease/sale, when).
  const historyGroups = () => {
    const groups: Record<string, HistoryGroup> = {};
    for (const r of history) {
      const key = `${r.proposed_customer_org_id}|${r.status}|${r.lease_or_sale || ''}|${r.reviewed_at}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          customer: r.customer_name,
          status: r.status,
          leaseOrSale: r.lease_or_sale,
          reviewer: r.reviewed_by_name,
          reviewedAt: r.reviewed_at,
          note: r.note,
          serials: [],
        };
      }
      groups[key].serials.push(r.drone_serial);
    }
    return Object.values(groups);
  };

  const refreshAfterReview = async () => {
    await load();
    // Only refetch history if it's already been loaded once (tab visited).
    if (historyLoaded) await loadHistory();
  };

  const approve = async (group: PendingGroup) => {
    const leaseOrSale = choices[group.key];
    if (!leaseOrSale) {
      toast.error('Choose Lease or Sale first');
      return;
    }
    setBusyKey(group.key);
    try {
      await api.post('/assignment-requests/approve', { body: { ids: group.ids, lease_or_sale: leaseOrSale } });
      toast.success(`Approved ${group.ids.length} drone(s) for ${group.customer} (${leaseOrSale})`);
      await refreshAfterReview();
    } catch (error: any) {
      toast.error(error?.message || 'Approve failed');
    } finally {
      setBusyKey(null);
    }
  };

  const reject = async (group: PendingGroup) => {
    setBusyKey(group.key);
    try {
      await api.post('/assignment-requests/reject', { body: { ids: group.ids } });
      toast.success(`Rejected ${group.ids.length} drone(s)`);
      await refreshAfterReview();
    } catch (error: any) {
      toast.error(error?.message || 'Reject failed');
    } finally {
      setBusyKey(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupList = useMemo(pendingGroups, [requests]);
  const pendingRows = useExiting(groupList, (g) => g.key);

  if (loading) return <LogoSpinner label="Loading transfer requests" />;

  const histList = historyGroups();

  const tabs = [
    { key: 'pending', label: `Pending${requests.length ? ` (${requests.length})` : ''}` },
    { key: 'history', label: 'History' },
  ];

  return (
    <PageLayout kind="workspace" className="pt-4">
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-primary">Drone Transfers</h1>
        <p className="text-on-surface-variant text-sm mt-0.5">
          Drones pilots marked for customers in the field, for your lease/sale decision.
        </p>
      </div>

      {/* Lease vs Sale records ownership, NOT an order — this trips people up. */}
      <Alert tone="info" live={false} title="Lease vs Sale">
        This only records how the customer holds these drones. It does <span className="text-on-surface">not</span> create a sale order.{' '}
        <span className="text-on-surface">Sale</span> means the organization owns them outright (e.g. drones
        returned or replaced after service that belong to their own fleet).{' '}
        <span className="text-on-surface">Lease</span> means they're on loan and stay in Firefly's internal pool.
      </Alert>

      <Tabs
        id="transfers"
        label="Transfer views"
        value={tab}
        onChange={key => setTab(key as 'pending' | 'history')}
        tabs={tabs}
      />

      {tab === 'pending' && (
        <TabPanel id="transfers" value="pending">
          <Reveal show={groupList.length === 0}>
            <EmptyState>No pending transfer requests.</EmptyState>
          </Reveal>

          {pendingRows.map(({ key, item: group, leaving }) => {
            const choice = choices[group.key];
            const busy = busyKey === group.key;
            return (
              <Reveal key={key} show={!leaving}>
              <div className="pb-5">
              <Panel
                level={3}
                title={<>Marked for <span className="text-primary">{group.customer || 'a customer'}</span></>}
                footer={
                <div className="flex flex-wrap items-center gap-3">
                  <SegTrack
                    label="Lease or sale"
                    value={choice || null}
                    onChange={key => setChoices((c) => ({ ...c, [group.key]: key as LeaseOrSale }))}
                    options={[
                      { key: 'lease', label: 'Lease', disabled: busy },
                      { key: 'sale', label: 'Sale', disabled: busy },
                    ]}
                  />
                  <div className="flex gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() => reject(group)}
                      disabled={busy}
                      className={buttonClass('quiet', 'md')}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => approve(group)}
                      disabled={busy || !choice}
                      className={buttonClass(choice === 'lease' ? 'info' : choice === 'sale' ? 'good' : 'filled', 'md')}
                    >
                      {busy ? 'Working…' : 'Approve'}
                    </button>
                  </div>
                </div>
                }
              >
                <p className="text-on-surface-variant text-xs -mt-2 mb-4">
                  by {group.pilot || 'unknown'} on {fmtDate(group.date)} · {group.serials.length} drone(s)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {group.serials.map((s) => (
                    <SerialChip key={s} serial={s} />
                  ))}
                </div>
              </Panel>
              </div>
              </Reveal>
            );
          })}
        </TabPanel>
      )}

      {tab === 'history' && (
        <TabPanel id="transfers" value="history" className="space-y-3">
          <Reveal show={!historyLoaded}>
            <SkeletonGroup label="Loading transfer history" className="space-y-3">
              <Skeleton className="h-20 w-full rounded-panel" />
              <Skeleton className="h-20 w-full rounded-panel" />
            </SkeletonGroup>
          </Reveal>
          <Reveal show={historyLoaded && histList.length === 0}>
            <EmptyState>No past transfers yet.</EmptyState>
          </Reveal>

          {histList.map((g) => {
            const approved = g.status === 'approved';
            return (
              <Panel
                key={g.key}
                level={3}
                padding="sm"
                title={approved ? (
                  <>
                    {g.leaseOrSale === 'sale' ? 'Sold' : 'Leased'} to{' '}
                    <span className="text-primary">{g.customer || 'a customer'}</span>
                  </>
                ) : (
                  <>
                    Rejected, <span className="text-on-surface-variant">{g.customer || 'a customer'}</span>
                  </>
                )}
                actions={
                  <Tag tone={approved ? 'good' : 'neutral'} className="shrink-0">
                    {approved ? 'Approved' : 'Rejected'}
                  </Tag>
                }
              >
                <p className="text-on-surface-variant text-xs mb-3">
                  {g.reviewer || 'unknown'} · {fmtDate(g.reviewedAt)} · {g.serials.length} drone(s)
                </p>

                <div className="flex flex-wrap gap-1.5">
                  {g.serials.map((s) => (
                    <SerialChip key={s} serial={s} />
                  ))}
                </div>

                {g.note && <p className="text-on-surface-variant text-xs italic mt-3">“{g.note}”</p>}
              </Panel>
            );
          })}
        </TabPanel>
      )}
    </div>
    </PageLayout>
  );
};

export default CustomerTransfers;
